import React, { useState, useRef, useEffect } from 'react';
import { Bell, Check, CheckCheck, Trash2, Upload, Brain, Radio, AlertCircle, Plug } from 'lucide-react';
import './NotificationBell.css';

const ICON_MAP = {
  classification: Brain,
  import: Upload,
  scraper: Radio,
  error: AlertCircle,
  connector: Plug,
};

const NotificationBell = ({ notifications = [], onMarkRead, onMarkAllRead, onClear }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  const unreadCount = notifications.filter(n => !n.read).length;

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    if (open) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [open]);

  return (
    <div className="notification-bell" ref={menuRef}>
      <button
        className={`notification-bell-trigger ${open ? 'open' : ''}`}
        onClick={() => setOpen(prev => !prev)}
        aria-expanded={open}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <span className="notification-header-title">Notifications</span>
            {notifications.length > 0 && (
              <div className="notification-header-actions">
                {unreadCount > 0 && (
                  <button
                    className="notification-header-btn"
                    onClick={() => onMarkAllRead?.()}
                    title="Mark all as read"
                  >
                    <CheckCheck size={14} />
                  </button>
                )}
                <button
                  className="notification-header-btn"
                  onClick={() => { onClear?.(); setOpen(false); }}
                  title="Clear all"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            )}
          </div>

          <div className="notification-list">
            {notifications.length > 0 ? (
              notifications.slice(0, 20).map(notification => {
                const Icon = ICON_MAP[notification.type] || Bell;
                return (
                  <button
                    key={notification.id}
                    className={`notification-item ${notification.read ? '' : 'unread'}`}
                    onClick={() => {
                      onMarkRead?.(notification.id);
                      // Deep-link notifications (e.g. connector health →
                      // Settings → Connections row) navigate on click.
                      if (typeof notification.onClick === 'function') {
                        setOpen(false);
                        notification.onClick();
                      }
                    }}
                  >
                    <div className={`notification-item-icon ${notification.type || ''}`}>
                      <Icon size={16} />
                    </div>
                    <div className="notification-item-content">
                      <p className="notification-item-text">{notification.message}</p>
                      <span className="notification-item-time">
                        {formatTime(notification.timestamp)}
                      </span>
                    </div>
                    {!notification.read && <div className="notification-unread-dot" />}
                  </button>
                );
              })
            ) : (
              <div className="notification-empty">
                <Bell size={24} />
                <p>No notifications</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function formatTime(timestamp) {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now - date;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

export default NotificationBell;
